import { Dialog, Transition } from "@headlessui/react";
import React, { Fragment, useEffect, useRef, useState } from "react";
import moment from "moment";
import AddButton from "../buttons/button";
import CloseIcon from "../../assets/icons/close-icon";
import DeleteIcon from "../../assets/icons/delete-icon";
import IndianRupeeIcon from "../../assets/icons/indian-rupee";
import LoadingOverlay from "../helper-components/overlay-loading";
import { useHotkeys } from "../helper-components/use-hotkeys";
import { api } from "../../redux/apis/api";
import successToast from "../helper-components/success-toast";
import errorToast from "../helper-components/error-toast";

// Records what a bill on credit (payment status Pending) was paid with and when.
// Opened from the bill list for users who can update bills (the backend checks it
// too). Laid out like the billing screen's payment panel (billing-payment-panel.js):
// pick a payment mode, adjust its amount, save. The amount moves off the bill's
// Credit line onto the modes picked here; a part payment leaves the rest on credit
// and the bill stays Pending.

// Credit is what is owed and CB spends the customer's credit balance: neither pays it off
const NON_SETTLING_MODES = ["credit", "cb"];
// the value format of a datetime-local input
const DATE_TIME_FORMAT = "YYYY-MM-DDTHH:mm";
// paise rounding slack when comparing rupee amounts
const AMOUNT_TOLERANCE = 0.005;
const MAX_TRANSACTION_ID_LENGTH = 255;
// the first nine payment modes have a number key, as on the billing screen
const MODE_HOTKEYS = "1,2,3,4,5,6,7,8,9";

const round2 = (amount) => Math.round((Number(amount) + Number.EPSILON) * 100) / 100;
const fmtINR = (amount) =>
  Number(amount || 0).toLocaleString("en-IN", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });

const labelClass =
  "min-h-[1.2rem] text-[var(--pay-met-lab-color)] text-[length:var(--pay-met-lab-fs)] font-[var(--pay-met-lab-fw)]";
const inputClass =
  "w-full min-h-[40px] px-2 border border-[var(--pay-met-input-border)] focus:outline-none bg-[var(--pay-met-input-bg)] text-[var(--pay-met-val-color)] text-[length:var(--pay-met-val-fs)] font-[var(--pay-met-val-fw)] border-radius input-field-no-spin-buttons";
const cardHeadClass =
  "bg-[var(--payment-summary-head-bg)] text-[var(--payment-summary-head-fg)] text-[length:var(--payment-summary-head-fs)] font-[var(--payment-summary-head-fw)] w-full h-[52px] flex items-center px-4 border-radius";
const summaryLabelClass =
  "text-[var(--payment-summary-label-fg)] text-[length:var(--payment-summary-label-fs)] font-[var(--payment-summary-label-fw)]";
const summaryValueClass =
  "text-[length:var(--payment-summary-value-fs)] font-[var(--payment-summary-value-fw)] tabular-nums";
const primaryButtonClass =
  "flex items-center px-[30px] h-[44px] whitespace-nowrap rounded-md gap-[8px] bg-[var(--primary-button-bg-color)] cursor-pointer text-[var(--primary-button-fg-color)] border border-[var(--primary-button-bg-color)] hover:bg-[var(--primary-button-hover-color)] text-[length:var(--primary-button-font-size)] font-[var(--primary-button-font-weight)]";

function PaymentModeButton({ mode, index, isUsed, disabled, buttonRef, onPick }) {
  return (
    <button
      ref={buttonRef}
      type="button"
      disabled={isUsed || disabled}
      onClick={() => onPick(mode)}
      className={`${
        isUsed
          ? "text-[var(--pay-type-con-disabled-fg)] cursor-not-allowed"
          : "text-[var(--pay-type-con-fg)]"
      } bg-[var(--pay-type-con-bg)] flex items-center justify-between gap-2 px-4 py-2 h-[44px] basis-[calc(33.333%-0.666rem)] max-w-[calc(33.333%-0.666rem)] rounded text-[length:var(--pay-type-con-fs)] font-[var(--pay-type-con-fw)] flex-grow-0 self-start whitespace-nowrap`}
    >
      <span className="flex items-center gap-2 min-w-0">
        <IndianRupeeIcon fill={isUsed ? "var(--pay-type-con-disabled-fg)" : "black"} />
        <span className="truncate">{mode}</span>
      </span>
      {index < 9 && <span>({index + 1})</span>}
    </button>
  );
}

function PaymentRow({ payment, index, disabled, onChange, onAmountChange, onRemove }) {
  const mode = payment.payment_mode_name;
  return (
    <div className="flex gap-3 items-end">
      <label className="w-[29%] flex flex-col">
        <span className={labelClass}>{mode}</span>
        <input
          type="number"
          min="0"
          step="0.01"
          value={payment.payment_amount}
          disabled={disabled}
          onChange={(e) => onAmountChange(index, e.target.value)}
          className={inputClass}
        />
      </label>
      {/* Cash has no transaction id, as on the billing screen */}
      {mode === "Cash" ? (
        <span className="flex-1" />
      ) : (
        <label className="flex-1 flex flex-col">
          <span className={labelClass}>Transaction Id</span>
          <input
            type="text"
            maxLength={MAX_TRANSACTION_ID_LENGTH}
            value={payment.transaction_id}
            disabled={disabled}
            onChange={(e) => onChange(index, { transaction_id: e.target.value })}
            className={inputClass}
          />
        </label>
      )}
      <button
        type="button"
        aria-label={`Remove ${mode}`}
        disabled={disabled}
        onClick={() => onRemove(index)}
        className="h-[40px] w-[40px] shrink-0 flex items-center justify-center bg-[var(--pay-type-del-bg)] border-radius"
      >
        <DeleteIcon />
      </button>
    </div>
  );
}

function EarlierPayments({ history }) {
  if (history.length === 0) return null;
  return (
    <div className="border border-[var(--slide-panel-border)] border-radius mt-4">
      <div className={cardHeadClass}>Earlier payments</div>
      <div className="divide-y divide-[#F1F5F9]">
        {history.map((entry) => (
          <div key={entry.bps_id} className="flex items-center justify-between px-4 py-2.5">
            <span className="flex flex-col gap-0.5">
              <span className="flex items-center gap-2 text-[13px] font-medium text-[#0F172A]">
                <IndianRupeeIcon fill="#475569" />
                {entry.payment_mode_name}
                {entry.transaction_id ? (
                  <span className="text-[11px] text-[#94A3B8] font-mono">{entry.transaction_id}</span>
                ) : null}
              </span>
              <span className="text-[11px] text-[#64748B]">
                Paid on {moment(entry.paid_at).format("DD MMM YYYY, hh:mm A")}
                {entry.created_by_name ? ` · recorded by ${entry.created_by_name}` : ""}
              </span>
            </span>
            <span className="text-[13px] font-semibold tabular-nums text-[#0F172A]">
              ₹ {fmtINR(entry.payment_amount)}
            </span>
          </div>
        ))}
      </div>
    </div>
  );
}

const SettleBillPaymentPanel = ({
  isOpen,
  sotId,
  billNumber = "",
  customerName = "",
  billDate = null,
  onClose = () => {},
  onSaved = () => {},
}) => {
  const [modes, setModes] = useState([]);
  const [creditDue, setCreditDue] = useState(0);
  // only a bill whose payment is still pending can be changed (the list may be stale)
  const [isPending, setIsPending] = useState(true);
  const [history, setHistory] = useState([]);
  const [payments, setPayments] = useState([]);
  const [paidAt, setPaidAt] = useState("");
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const modeRefs = useRef([]);
  const saveRef = useRef(null);
  const closeRef = useRef(null);

  useHotkeys(MODE_HOTKEYS, (event) => modeRefs.current[Number(event.key) - 1]?.click(), {
    enabled: isOpen,
  });
  useHotkeys("s", () => saveRef.current?.click(), { enabled: isOpen });
  useHotkeys("x", () => closeRef.current?.click(), { enabled: isOpen });

  useEffect(() => {
    if (!isOpen || !sotId) return;
    let cancelled = false;
    setLoading(true);
    setIsPending(true);
    setCreditDue(0);
    setPayments([]);
    setHistory([]);
    setPaidAt(moment().format(DATE_TIME_FORMAT));
    Promise.all([
      api.get(`/paymentsModes/get`),
      api.get(`/order/payment-settlements/${sotId}`),
    ])
      .then(([modesRes, billRes]) => {
        if (cancelled) return;
        setModes(
          (modesRes?.data?.data?.paymentModes ?? [])
            .map((mode) => mode?.paymentType)
            .filter((name) => name && !NON_SETTLING_MODES.includes(name.trim().toLowerCase()))
        );
        setCreditDue(Number(billRes?.data?.data?.credit_due) || 0);
        setIsPending(billRes?.data?.data?.is_pending === true);
        setHistory(billRes?.data?.data?.settlements ?? []);
      })
      .catch((err) => {
        if (!cancelled) {
          errorToast(err?.response?.data?.message ?? "Could not load the bill's payment details");
        }
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [isOpen, sotId]);

  const paying = round2(
    payments.reduce((sum, payment) => sum + (Number(payment?.payment_amount) || 0), 0)
  );
  const leftOnCredit = round2(creditDue - paying);
  const isOverpaid = leftOnCredit < -AMOUNT_TOLERANCE;
  const usedModes = payments.map((payment) => payment.payment_mode_name);
  const canPay = !loading && isPending && creditDue > AMOUNT_TOLERANCE;

  const updatePayment = (index, changes) =>
    setPayments((current) =>
      current.map((payment, i) => (i === index ? { ...payment, ...changes } : payment))
    );
  // a picked mode starts with whatever is still on credit, as on the billing screen
  const addPayment = (mode) => {
    if (usedModes.includes(mode)) return;
    setPayments((current) => [
      ...current,
      { payment_mode_name: mode, payment_amount: Math.max(leftOnCredit, 0), transaction_id: "" },
    ]);
  };
  const removePayment = (index) =>
    setPayments((current) => current.filter((_, i) => i !== index));
  // the payments together can't go past the credit due
  const changeAmount = (index, value) => {
    const othersTotal = payments.reduce(
      (sum, payment, i) => (i === index ? sum : sum + (Number(payment.payment_amount) || 0)),
      0
    );
    const maxForRow = Math.max(round2(creditDue - othersTotal), 0);
    if ((Number(value) || 0) > maxForRow) {
      errorToast("Amount cannot exceed the credit due");
      updatePayment(index, { payment_amount: maxForRow });
      return;
    }
    updatePayment(index, { payment_amount: value });
  };

  const validationError = () => {
    if (payments.length === 0) return "Pick how the customer paid";
    const invalid = payments.find((payment) => !(round2(payment.payment_amount) > 0));
    if (invalid) return `Enter an amount above ₹0 for ${invalid.payment_mode_name}`;
    if (isOverpaid) return `₹${fmtINR(paying)} is more than the ₹${fmtINR(creditDue)} credit due`;
    if (!paidAt || !moment(paidAt, DATE_TIME_FORMAT, true).isValid()) return "Enter when the payment was made";
    return null;
  };

  const handleSave = async () => {
    const error = validationError();
    if (error) {
      errorToast(error);
      return;
    }
    setSaving(true);
    try {
      const res = await api.post(`/order/settle-payment`, {
        sot_id: sotId,
        payments: payments.map((payment) => ({
          payment_mode_name: payment.payment_mode_name,
          payment_amount: round2(payment.payment_amount),
          transaction_id: payment.transaction_id?.trim() || null,
        })),
        paid_at: moment(paidAt, DATE_TIME_FORMAT).toISOString(),
      });
      successToast(res?.data?.message ?? "Payment recorded");
      onSaved(res?.data?.data);
      onClose();
    } catch (err) {
      errorToast(err?.response?.data?.message ?? "Could not record the payment");
    } finally {
      setSaving(false);
    }
  };

  const footerNote = !canPay
    ? ""
    : payments.length === 0
    ? "Pick a payment mode to record what was paid"
    : leftOnCredit > AMOUNT_TOLERANCE
    ? `₹ ${fmtINR(leftOnCredit)} stays on credit and the bill stays Pending`
    : "The bill will be marked Paid";

  return (
    <Transition.Root show={!!isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={() => !saving && onClose()}>
        <div className="fixed inset-0 overflow-hidden">
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <Dialog.Overlay className="absolute inset-0 bg-[#0000004D]" />
          </Transition.Child>

          <div className="fixed inset-0 flex justify-end">
            <Transition.Child
              as={Fragment}
              enter="transform transition ease-in-out duration-300"
              enterFrom="translate-x-full"
              enterTo="translate-x-0"
              leave="transform transition ease-in-out duration-300"
              leaveFrom="translate-x-0"
              leaveTo="translate-x-full"
            >
              {/* Header / scrolling body / footer: only the body scrolls */}
              <Dialog.Panel className="h-full relative w-[400px] sm:w-[600px] bg-white flex flex-col overflow-hidden rounded-tl-lg rounded-bl-lg shadow-xl">
                <div
                  className="h-[61px] shrink-0 bg-[var(--slide-panel-head-bg)] px-3 flex items-center justify-between gap-3 border-b"
                  style={{ borderColor: "var(--slide-panel-border)" }}
                >
                  <div className="min-w-0 flex flex-col">
                    <span
                      style={{
                        color: "var(--slide-panel-h-fg)",
                        fontSize: "var(--slide-panel-h-fs)",
                        fontWeight: "var(--slide-panel-h-fw)",
                      }}
                    >
                      Record Payment
                    </span>
                    {(billNumber || customerName) && (
                      <span className="text-[12px] text-[#64748B] truncate">
                        {billNumber && `Bill No: ${billNumber}`}
                        {billNumber && customerName && " · "}
                        {customerName}
                      </span>
                    )}
                  </div>
                  <AddButton
                    ref={closeRef}
                    isDisabled={saving}
                    onclkFn={onClose}
                    icon={<CloseIcon width="12px" height="12px" fill="var(--close-button-fg)" />}
                    text={
                      <span>
                        Close <span className="opacity-50">(X)</span>{" "}
                      </span>
                    }
                    className="flex h-[32px] shrink-0 items-center gap-2 bg-[var(--close-button-bg)] border-radius px-3 text-[var(--close-button-fg)]"
                  />
                </div>

                <div className="flex-1 min-h-0 px-3 py-4 relative overflow-y-auto">
                  <div className="h-[5.12rem] w-full flex items-center">
                    <div className="bg-[var(--net-payable-con-bg)] min-h-[52px] flex items-center px-4 justify-between rounded-[8px] w-full">
                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-label-fw)]">
                        Credit due amount
                      </span>
                      <span className="text-[var(--net-payable-text-color)] text-[length:var(--net-payable-fs)] font-[var(--net-payable-value-fw)] tabular-nums">
                        ₹ {fmtINR(creditDue)}
                      </span>
                    </div>
                  </div>

                  {!loading && !isPending && (
                    <div className="border border-[var(--slide-panel-border)] border-radius px-4 py-3 text-[13px] text-[#F94B4B]">
                      Only bills with a pending payment can be changed. This bill's
                      payment is already complete.
                    </div>
                  )}

                  {canPay && modes.length === 0 && (
                    <div className="border border-[var(--slide-panel-border)] border-radius px-4 py-3 text-[13px] text-[#F94B4B]">
                      No payment modes other than Credit are set up for this store.
                    </div>
                  )}

                  {canPay && modes.length > 0 && (
                    <>
                      <div className="border border-[var(--slide-panel-border)] rounded">
                        <div className="flex flex-wrap gap-x-4 gap-y-4 p-4">
                          {modes.map((mode, index) => (
                            <PaymentModeButton
                              key={mode}
                              mode={mode}
                              index={index}
                              isUsed={usedModes.includes(mode)}
                              disabled={saving}
                              buttonRef={(el) => {
                                modeRefs.current[index] = el;
                              }}
                              onPick={addPayment}
                            />
                          ))}
                        </div>
                      </div>

                      <div className="border border-[var(--slide-panel-border)] border-radius mt-4">
                        <div className={cardHeadClass}>Payment Summary</div>
                        {payments.length > 0 && (
                          <div className="flex flex-col gap-3 px-4 pt-4">
                            {payments.map((payment, index) => (
                              <PaymentRow
                                key={payment.payment_mode_name}
                                payment={payment}
                                index={index}
                                disabled={saving}
                                onChange={updatePayment}
                                onAmountChange={changeAmount}
                                onRemove={removePayment}
                              />
                            ))}
                          </div>
                        )}

                        <div className="p-4 flex flex-col gap-[12px]">
                          <label className="flex flex-col">
                            <span className={labelClass}>Payment received on</span>
                            <input
                              type="datetime-local"
                              value={paidAt}
                              min={billDate ? moment(billDate).format(DATE_TIME_FORMAT) : undefined}
                              max={moment().format(DATE_TIME_FORMAT)}
                              disabled={saving}
                              onChange={(e) => setPaidAt(e.target.value)}
                              className={inputClass}
                            />
                          </label>

                          <div className="border border-[var(--slide-panel-border)] p-2 flex flex-col border-radius">
                            <div className="flex justify-between border-b border-b-[var(--slide-panel-border)] p-3">
                              <span className={summaryLabelClass}>Credit Due</span>
                              <span className={`text-[var(--payment-summary-value-fg)] ${summaryValueClass}`}>
                                ₹ {fmtINR(creditDue)}
                              </span>
                            </div>
                            {paying > 0 && (
                              <div className="flex justify-between border-b border-b-[var(--slide-panel-border)] px-3 py-2">
                                <span className="text-[12px] text-[#64748B]">Paying now</span>
                                <span className="text-[12px] text-[#64748B] tabular-nums">
                                  ₹ {fmtINR(paying)}
                                </span>
                              </div>
                            )}
                            <div className="flex justify-between p-3">
                              <span className={summaryLabelClass}>Remaining on Credit</span>
                              <span
                                className={`${
                                  isOverpaid ? "text-[#F94B4B]" : "text-[var(--payment-summary-value-fg)]"
                                } ${summaryValueClass}`}
                              >
                                ₹ {fmtINR(leftOnCredit)}
                              </span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </>
                  )}

                  <EarlierPayments history={history} />

                  <LoadingOverlay isLoading={loading || saving} />
                </div>

                <div className="h-[5rem] shrink-0 flex justify-between items-center gap-3 px-3 w-full bg-white border-t border-[var(--slide-panel-border)] shadow-[0_-4px_12px_-6px_rgba(16,24,40,0.12)]">
                  <span className="text-[13px] text-[#64748B]">{footerNote}</span>
                  <AddButton
                    ref={saveRef}
                    isDisabled={!canPay || modes.length === 0 || payments.length === 0}
                    loading={saving}
                    onclkFn={handleSave}
                    prefixIcon={false}
                    text={
                      <span>
                        Save <span className="opacity-50">(S)</span>{" "}
                      </span>
                    }
                    className={primaryButtonClass}
                  />
                </div>
              </Dialog.Panel>
            </Transition.Child>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};

export default SettleBillPaymentPanel;
